import { forwardRef, useRef, useCallback, useState, ChangeEvent, FocusEvent, FocusEventHandler, ReactNode } from "react";
import { isDescendentOf } from "../../utilities/isDescendentOf";
import classNames from "classnames";
import { InputChangeHandler } from "../InputChangeHandler";
import { useValidity } from "../ValidityGroup";
import { useSubmitting } from "../SubmitGroup";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import { getStyleModeSafeProps } from "../utils/getStyleModeSafeProps";
import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedDotStyles = createComponentStyleHook<DotProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "radio",
});

/**
 * The `Dot` component properties.
 */
export interface DotProps {
  /**
   * The input ID.
   */
  readonly id: string;

  /**
   * Indicates whether the input is disabled.
   */
  readonly disabled?: boolean;

  /**
   * Indicates whether the input is read-only.
   */
  readonly readOnly?: boolean;

  /**
   * The tab index.
   */
  readonly tabIndex?: number;

  /**
   * Indicates whether to focus the input on initialization.
   */
  readonly autoFocus?: boolean;

  /**
   * The value.
   */
  readonly value: boolean | null;

  /**
   * The default value.
   */
  readonly defaultValue: boolean | null;

  /**
   * Instructions displayed after the input.
   */
  readonly instructions?: ReactNode;

  /**
   * The function to call when the value is to be changed.
   */
  readonly onChange?: InputChangeHandler<boolean | null>;

  /**
   * The function to call when the input is focused.
   */
  readonly onFocus?: FocusEventHandler;

  /**
   * The function to call when the input loses focus.
   */
  readonly onBlur?: FocusEventHandler;
}

/**
 * The dot component.
 */
export const Dot = forwardRef<HTMLInputElement, DotProps>(function Dot(props, ref) {
  const { id, disabled = false, readOnly = false, tabIndex, autoFocus, defaultValue = null, instructions, onChange, onFocus, onBlur } = props;

  const { value = defaultValue } = props;

  const selected = value === true;

  const submitting = useSubmitting();
  const [invalid] = useValidity();
  const { mode, styles, StyleModeBoundary } = useResolvedDotStyles(getStyleModeSafeProps(props));

  const checkContainerRef = useRef<HTMLSpanElement | null>(null);
  const [hover, setHover] = useState(false);
  const labelMouseEnterHandler = useCallback((ev: Event) => {
    setHover(true);
    ev.stopPropagation();
  }, []);

  const labelMouseExitHandler = useCallback((ev: Event) => {
    setHover(false);
    ev.stopPropagation();
  }, []);

  const checkContainerRefHandler = useCallback((ref: HTMLSpanElement | null) => {
    if (checkContainerRef.current) {
      checkContainerRef.current.removeEventListener("cust_labelMouseEnter", labelMouseEnterHandler);
      checkContainerRef.current.removeEventListener("cust_labelMouseExit", labelMouseExitHandler);
    }

    if (ref) {
      ref.addEventListener("cust_labelMouseEnter", labelMouseEnterHandler);
      ref.addEventListener("cust_labelMouseExit", labelMouseExitHandler);
    } else {
      setHover(false);
    }

    checkContainerRef.current = ref;
  }, []);

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(ev.target.checked, value);
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const blurHandler = useCallback(
    (ev: FocusEvent) => {
      const { currentTarget, relatedTarget } = ev;

      /*
      If for some reason our focus is moving to a different element (like, let's say, the label), don't raise the focus
      exit event, because we're actually going to retain the focus on the element.
    */
      if (relatedTarget && currentTarget.parentElement && isDescendentOf(currentTarget.parentElement, relatedTarget)) {
        ev.preventDefault();
        return;
      }

      onBlur?.(ev);
    },
    [onBlur],
  );

  return (
    <StyleModeBoundary mode={mode}>
      <div
        ref={checkContainerRefHandler}
        className={classNames(styles.Dot, "dot", {
          selected,
          disabled,
          invalid,
          readonly: readOnly || submitting,
          hover,
        })}
      >
        <input
          id={id}
          ref={ref}
          className="input"
          aria-checked={selected}
          aria-disabled={disabled}
          aria-labelledby={`${id}-label`}
          aria-describedby={instructions ? `${id}-instructions` : undefined}
          type="radio"
          tabIndex={readOnly || disabled || submitting ? -1 : tabIndex ?? 0}
          checked={selected}
          disabled={disabled}
          autoFocus={autoFocus}
          readOnly={readOnly || submitting}
          onBlur={blurHandler}
          onFocus={onFocus}
          onChange={changeHandler}
        />
        <div className="dot-container" aria-hidden="true">{selected && <div className="indicator" />}</div>
      </div>
    </StyleModeBoundary>
  );
});
